import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { check } from 'meteor/check';
import { DeclareMethod } from '/imports/methods';
import { Dict, Doc, FileMeta, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateAddress, validateArray, validateBoolean, validateNumber, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { GmapsAddress } from '/imports/gmaps';
import { Destination } from '/server/integrations/gmaps';
import { Account } from '/imports/api/accounts';
import type { PvWattsResponse } from '/server/integrations/pvwatts';
import { Orgs } from '/imports/api/orgs';
import { ProjectQuote } from '/imports/api/projectQuotes';

export interface LoadProfileProvenance {
  startDate: Date;          // first interval; the curve is day-rotated to align to this year's Jan 1
  year: number;             // calendar year the curve was aligned to
  randomized: boolean;      // true = synthetic Math.random() fallback (NOT reproducible)
  mode?: 'rescaled' | 'determined'; // how the load magnitude was set
  dataSource?: string;      // engine source, e.g. 'ComStock by_county …', 'CBECS …', 'MECS …'
  sector?: string;          // 'commercial' | 'lightIndustrial' | 'heavyIndustrial'
  buildingType?: string;    // resolved ComStock / profile building type
  countyId?: string;        // GISJOIN — the ComStock store's county fetch key
  climateZone?: string;     // ASHRAE IECC 2006 zone (fallback donor key)
  state?: string;           // 2-letter
  comstockRelease?: string; // pinned ComStock release id
  fallbacksUsed?: string[]; // engine fallbacks applied during resolution
}

export interface Project extends Doc {
  orgId: string;
  name: string;
  proposalType?: 'ess' | 'essSolar';
  status?: 'initial' | 'new' | 'lead' | 'marketingProposal' | 'projectData' | 'preliminaryProposal' | 'siteSurvey' | 'detailedProposal' | 'saleCompleted';
  enriched?: boolean;
  upgrades?: string[];
  address?: GmapsAddress;
  systemSize?: number;
  annualProduction?: number;
  annualConsumption?: number;
  maxDemand?: number;
  epcPrice?: number;
  avoidedCost?: number;
  installType?: 'rooftop' | 'groundMount' | 'carport' | 'multiple';
  buildingTypes?: string[];
  ownershipType?: 'owner' | 'tenant'; // | 'employee';
  enrichmentStatus?: string;
  utilities?: string[];
  ccas?: string[];
  county?: string;
  essCapacity?: number;
  essSize?: number;
  // Fields the user has typed in at some point. The `manuallyEntered` list passed around enrichment only
  // describes the save that just happened, so without this a later force-enrich has no idea the battery
  // was chosen rather than derived, and goes back to sweeping every size. Read by the ETB ESS sweep only
  // — it does not gate enrichment generally.
  manualFields?: string[];
  essEpcPrice?: number;
  essAvoidedCost?: number;
  // Energy Toolbase automation result (server/integrations/etb.ts). solarAvoidedCost is always present;
  // the rest are optional and persisted partially — an ETB run can complete without an ESS winner or a scraped cost stat.
  etb?: {
    solarAvoidedCost: number;
    essAvoidedCost?: number;
    pvCostPerWdc?: number;
    essCostPerKwh?: number;
    winningStrategy?: { essCapacity: number; essSize: number; config: 'stacked' | 'ea' };
    // Rate-switch reconciliation: when ETB's blended $/kWh values didn't add up to the bill savings, the
    // separately-reported rate-switch savings were folded into solarAvoidedCost (rateSwitchApplied=true).
    rateSwitchSavings?: number;
    rateSwitchPerKwh?: number;
    rateSwitchApplied?: boolean;
    simulatedIntervals?: FileMeta; // simulated 15-min load profile fed to ETB, as .csv
    // The inputs ETB used to produce the results above (utility, tariffs, PV/load-profile params).
    inputs?: {
      utility?: string;
      rateTariff?: string;
      cca?: string;
      ccaTariff?: string;
      systemSize?: number;
      rackingType?: string;
      annualConsumption?: number;
      intervalDataMode?: 'rescaled' | 'determined';
      buildingType?: string;
    };
    provenance?: LoadProfileProvenance;
  };
  notes?: string;
  image?: FileMeta;
  ptoYear?: number;
  solarEpcItcEligible?: boolean;
  solarEnergyCommunityAdder?: boolean;
  solarDomesticContentAdder?: boolean;
  storageEpcItcEligible?: boolean;
  storageEnergyCommunityAdder?: boolean;
  storageDomesticContentAdder?: boolean;
  siteLeasePayment?: string;
  investmentTaxCreditAdder?: number;
  solarPpaEscalator?: number;
  ppaTermLength?: number;
  avoidedCostEscalator?: number;
  buyoutYear1?: number;
  buyoutYear2?: number;
  solarSystemIncentive?: number;
  energyStorageIncentive?: number;
  otherIncentive?: number;
  incentiveTerm?: number;
  annualIncentiveEscalation?: number;
  incentiveRate?: number;
  rooftopArea?: number;
  rooftopAreaSource?: string;
  sunroof?: { sqft: number, imagePath: string, source?: string, maxKwDc?: number, existingSolar?: boolean };
  footprintArea?: number;
  gmapsDestinationId?: string;
  _gmapsDestination?: Destination['primary'] & { addressString: string, footprintArea?: number };
  pvWattsId?: string;
  _pvWatts?: PvWattsResponse['outputs'];
  reonomyId?: string;
  _reonomy?: any;
  reportedOwners?: { entity_type: string, name: string }[];
  assignedToAccountId?: string;
  _assignedToAccount?: Account;
  helioscopeLink?: string;
  // agreement fields
  lessorName?: string;
  lessorEmail?: string;
  lessorCompany?: string;
  lessorCompanyParent?: string;
  lessorAddress1?: string;
  lessorAddress2?: string;
  lessorAttentionName?: string;
  lessorAttentionEmail?: string;
  lesseeName?: string;
  lesseeEmail?: string;
  lesseeCompany?: string;
  lesseeCompanyParent?: string;
  propertyOwnerEmail?: string;
  propertyOwnerName?: string;
  tenantEmail?: string;
  tenantName?: string;
  ceaEmail?: string;
  ceaName?: string;
  leaseEffectiveDate?: string;
  meterNumbers?: string;
  ptoDate?: number;
  initialRate?: number;
  initialRateUpdated?: number;
  termEndDate?: string;
  renewalTermYears?: number;
  maxTermEndDate?: string;
  firstRentPayment?: number;
  proCo?: string;
  propertyLegalDescription?: string;
  easementsMetesAndBounds?: string;
  customerUtilityMeterNumbers?: string;
  propertyApn?: string;
  agreementExpirationDate?: string;
  hubspotObjectId?: string;
  // end agreement fields
}
export type Model = Project;

export const Projects = new Mongo.Collection<Project>('projects');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: Projects,
  editableFields: ['active', 'orgId', 'name', 'status', 'upgrades', 'address', 'county', 'ccas', 'utilities', 'ownershipType', 'systemSize',
    'assignedToAccountId', 'annualProduction', 'epcPrice', 'avoidedCost', 'installType', 'buildingTypes', 'essCapacity', 'essSize', 'essEpcPrice',
    'notes', 'image', 'investmentTaxCreditAdder', 'solarPpaEscalator', 'ppaTermLength', 'avoidedCostEscalator',
    'buyoutYear1', 'buyoutYear2', 'solarSystemIncentive', 'energyStorageIncentive', 'otherIncentive', 'incentiveTerm',
    'annualIncentiveEscalation', 'incentiveRate', 'annualConsumption', 'maxDemand', 'essAvoidedCost', 'ownershipType', 'helioscopeLink',
    'ptoYear', 'solarEpcItcEligible', 'solarEnergyCommunityAdder', 'solarDomesticContentAdder',
    'storageEpcItcEligible', 'storageEnergyCommunityAdder', 'storageDomesticContentAdder', 'siteLeasePayment',
    // agreement fields
    'lessorEmail', 'lessorName', 'lesseeEmail', 'lesseeName', 'leaseEffectiveDate', 'lessorCompany', 'lessorCompanyParent', 'lessorAddress1', 'lessorAddress2',
    'lessorAttentionName', 'lessorAttentionEmail', 'lesseeCompany', 'lesseeCompanyParent', 'meterNumbers', 'ptoDate', 'initialRate', 'initialRateUpdated',
    'termEndDate', 'renewalTermYears', 'maxTermEndDate', 'firstRentPayment', 'proCo', 'propertyLegalDescription', 'easementsMetesAndBounds',
    'customerUtilityMeterNumbers', 'propertyApn', 'agreementExpirationDate', 'propertyOwnerEmail', 'propertyOwnerName', 'tenantEmail', 'tenantName',
    'ceaEmail', 'ceaName', '_enrich'
    // end agreement fields
  ],
  fieldLimits: {
    'agreementExpirationDate': 64, 'assignedToAccountId': 64, 'buildingTypes': 256, 'ccas': 128, 'ceaEmail': 320,
    'ceaName': 256, 'county': 256, 'customerUtilityMeterNumbers': 1024, 'easementsMetesAndBounds': 20000,
    'helioscopeLink': 2048, 'installType': 64, 'leaseEffectiveDate': 64, 'lesseeCompany': 256, 'lesseeCompanyParent': 256,
    'lesseeEmail': 320, 'lesseeName': 256, 'lessorAddress1': 256, 'lessorAddress2': 256, 'lessorAttentionEmail': 320,
    'lessorAttentionName': 256, 'lessorCompany': 256, 'lessorCompanyParent': 256, 'lessorEmail': 320, 'lessorName': 256,
    'maxTermEndDate': 64, 'meterNumbers': 1024, 'name': 256, 'notes': 50000, 'orgId': 64, 'ownershipType': 64, 'proCo': 256,
    'propertyApn': 1024, 'propertyLegalDescription': 50000, 'propertyOwnerEmail': 320, 'propertyOwnerName': 256,
    'siteLeasePayment': 256, 'status': 64, 'tenantEmail': 320, 'tenantName': 256, 'termEndDate': 64, 'upgrades': 64,
    'utilities': 256,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<Project>, ListMethodResponse<Project, Refs>>('listProjects'),
  getMethod: DeclareMethod<string, GetMethodResponse<Project, Refs>>('getProject'),
  createMethod: DeclareMethod<Partial<Project>, string>('createProject'),
  updateMethod: DeclareMethod<Partial<Project>, string>('updateProject'),
  deleteMethod: DeclareMethod<string>('deleteProject'),
  pathPrefix: '/projects',
  asString: (document) => document.name || document._id,
  modelName: strings.Project(),
  pluralName: strings.Projects(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
      ]
    };
  },
  validate: async (project: Partial<Project>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'orgId', { required: true });
    validateString(errors, project, 'name', { required: true });
    // validateString(errors, project, 'proposalType', { required: true });
    project.upgrades && check(project.upgrades, [String]);
    if (!project._skipValidation) {
      validateAddress(errors, project, 'address', { required: true });
      validateNumber(errors, project, 'systemSize');
      validateNumber(errors, project, 'annualProduction');
      validateNumber(errors, project, 'annualConsumption');
      validateNumber(errors, project, 'epcPrice');
      validateNumber(errors, project, 'avoidedCost');
      validateString(errors, project, 'installType');
      validateArray(errors, project, 'buildingTypes');
      validateNumber(errors, project, 'essCapacity');
      validateNumber(errors, project, 'essSize');
      validateNumber(errors, project, 'essEpcPrice');
      validateNumber(errors, project, 'essAvoidedCost');
    }
    project.notes && check(project.notes, String);
    const org = await Orgs.findOneAsync(project.orgId);
    if (org?.projectChannel === 'dealer' && !project._skipValidation && !['initial', 'new'].includes(project.status || 'new')) {
      validateNumber(errors, project, 'epcPrice', { required: true });
      validateNumber(errors, project, 'essEpcPrice', { required: true });
    }
    return errors;
  },
};

export const enrichProject = DeclareMethod<{ _id: string, force?: boolean, manuallyEntered?: string[] }, void>('enrichProject');
export const validateProjectForQuote = DeclareMethod<{ projectId: string, proposalType: ProjectQuote['proposalType'] }, string[]>('validateProjectForQuote');
export const applyConsumptionMetersToProject = DeclareMethod<{ projectId: string, meterIds: string[] }, void>('applyConsumptionMetersToProject');

Meteor.isServer && Meteor.startup(async () => {
  await Projects.createIndexAsync({ orgId: 1 });
  await Projects.createIndexAsync({ name: 1 });
});

export const InstallTypeOptions = [
  { value: 'rooftop', label: strings.Rooftop() },
  { value: 'groundMount', label: strings.GroundMount() },
  { value: 'carport', label: strings.Carport() },
  { value: 'multiple', label: strings.Multiple() },
];

export const BuildingTypeOptions = [
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'coldStorage', label: 'Cold Storage' },
  { value: 'distribution', label: 'Distribution Center' },
  { value: 'lightManufacturing', label: 'Light Manufacturing' },
  { value: 'heavyManufacturing', label: 'Heavy Manufacturing' },
  { value: 'grocertyStore', label: 'Grocery Store' },
  { value: 'retailShopping', label: 'Retail / Shopping Center' },
  { value: 'church', label: 'Church' },
  { value: 'school', label: 'School' },
  { value: 'commercialOffice', label: 'Commercial Office' },
  { value: 'multiFamilyResi', label: 'Multi-Family Residential' },
  { value: 'resortHotel', label: 'Resort / Hotel' },
  { value: 'generalClassAOffice', label: 'General Class A Office' },
  { value: 'medicalOffice', label: 'Medical Office' },
  { value: 'communityCenter', label: 'Community Center' },
  { value: 'classCOffice', label: 'Class C Office' },
  { value: 'sportsComplex', label: 'Sports Complexes / Gyms' },
  { value: 'parkingGarage', label: 'Parking Garages' },
  { value: 'hospital', label: 'Hospitals or Nursing Facilities' },
  { value: 'nonProfit', label: 'Not-for-profits / Civic Properties' },
  { value: 'dataCenter', label: 'Data Center' },
  { value: 'marinaCountryClub', label: 'Marinas or County Club' },
  { value: 'autoDealerRepair', label: 'Auto Dealerships / Autobody Repair' },
  { value: 'orchard', label: 'Orchard' },
  { value: 'other', label: 'Other / Not Listed' },
];

export const OwnershipTypeOptions = [
  { value: 'owner', label: strings.PropertyOwnerOwnerUserPartner() },
  // { value: 'employee', label: strings.EmployeeRepresentativeOfOwner() },
  { value: 'tenant', label: strings.NonOwnerTenant() },
];

/** True if ESS capacity is below 80% of system size (or either is unset). */
export const essCapacitySystemSizeIsValid = (project: Partial<Project>) => {
  const { essCapacity, systemSize } = project;
  // if (essCapacity && systemSize && 0.5 * essCapacity > 0.8 * systemSize) return false; // old rule
  if (essCapacity && systemSize && essCapacity >= 0.8 * systemSize) return false;
  return true;
};

/** True if EPC price ($/W) is within the 1-5 range. */
export const epcPriceIsValid = (project: Partial<Project>) => {
  const { epcPrice } = project;
  if (!epcPrice || epcPrice < 1 || epcPrice > 5) return false;
  return true;
};

/** True if avoided cost ($/kWh) is within the 0.07-1 range. */
export const avoidedCostIsValid = (project: Partial<Project>) => {
  const { avoidedCost } = project;
  if (!avoidedCost || avoidedCost < 0.07 || avoidedCost > 1) return false;
  return true;
};

/** True if specific energy (annualProduction / systemSize) is within the 1100-2200 kWh/kW range. */
export const specificEnergyIsValid = (project: Partial<Project>) => {
  const { systemSize, annualProduction } = project;
  if (!systemSize || !annualProduction) return false;
  const specificEnergy = annualProduction / systemSize;
  if (specificEnergy < 1100 || specificEnergy > 2200) return false;
  return true;
};

/** True if ESS capacity is at least 120. */
export const essCapacityIsValid = (project: Partial<Project>) => {
  const { essCapacity } = project;
  if (!essCapacity || essCapacity < 120) return false;
  return true;
};

/** True if ESS size is at least 60 and not greater than ESS capacity. */
export const essSizeIsValid = (project: Partial<Project>) => {
  const { essSize, essCapacity } = project;
  if (!essSize || !essCapacity || essSize < 60 || essSize > essCapacity) return false;
  return true;
};

/** True if ESS EPC price is at least 200. */
export const essEpcPriceIsValid = (project: Partial<Project>) => {
  const { essEpcPrice } = project;
  if (!essEpcPrice || essEpcPrice < 200) return false;
  return true;
};

/** Localized display label for a project's status. */
export const projectStatus = (project: Partial<Project>) => {
  if (project.status === 'new') return strings.New();
  if (project.status === 'lead') return strings.Lead();
  if (project.status === 'marketingProposal') return strings.MarketingProposal();
  if (project.status === 'projectData') return strings.ProjectData();
  if (project.status === 'preliminaryProposal') return strings.PreliminaryProposal();
  if (project.status === 'siteSurvey') return strings.SiteSurvey();
  if (project.status === 'detailedProposal') return strings.DetailedProposal();
  if (project.status === 'saleCompleted') return strings.SaleCompleted();
  return project.status;
};

/** Localized display label for a project's install type. */
export const projectInstallType = (project: Partial<Project>) => {
  if (project.installType === 'rooftop') return strings.Rooftop();
  if (project.installType === 'groundMount') return strings.GroundMount();
  if (project.installType === 'carport') return strings.Carport();
  if (project.installType === 'multiple') return strings.Multiple();
};

// ESS size (kW) → base capacity (kWh) = the smallest 313 kWh block count that size supports. Doubles as
// the source of the ESS size dropdown (its keys), so every size here needs a row in essCapacityOptions
// below, and each value must equal that row's first option.
export const twoHourBatteryMap: Dict<number> = {
  80: 313, 150: 313, 300: 626, 450: 939, 600: 1252, 750: 1565
};

/**
 * Valid ESS capacity dropdown options for a given ESS size: the base capacity plus 2×, 3× and 4× it
 * (stacking additional battery units). Keep the sizes and each row's first value in step with
 * twoHourBatteryMap — the size dropdown is built from that map's keys, so a size listed there without a
 * row here yields an empty capacity dropdown.
 * @param essSize - the selected ESS size
 * @returns array of { value, label } capacity options
 */
export const essCapacityOptions = (essSize?: number) => {
  if (!essSize) return [];
  if (essSize === 80) return [{ value: '313', label: '313' }, { value: '626', label: '626' }, { value: '939', label: '939' }, { value: '1252', label: '1252' }];
  if (essSize === 150) return [{ value: '313', label: '313' }, { value: '626', label: '626' }, { value: '939', label: '939' }, { value: '1252', label: '1252' }];
  if (essSize === 300) return [{ value: '626', label: '626' }, { value: '1252', label: '1252' }, { value: '1878', label: '1878' }, { value: '2504', label: '2504' }];
  if (essSize === 450) return [{ value: '939', label: '939' }, { value: '1878', label: '1878' }, { value: '2817', label: '2817' }, { value: '3756', label: '3756' }];
  if (essSize === 600) return [{ value: '1252', label: '1252' }, { value: '2504', label: '2504' }, { value: '3756', label: '3756' }, { value: '5008', label: '5008' }];
  if (essSize === 750) return [{ value: '1565', label: '1565' }, { value: '3130', label: '3130' }, { value: '4695', label: '4695' }, { value: '6260', label: '6260' }];
  return [];
};
